import { Lock, ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { MoodFeelingCollapsible } from "./mood-feeling-collapsible";

export interface MoodFeelingCardProps {
  moodOptions: Array<any>;
  feelingOptions: Array<any>;
  feelingsByMood: Map<string, Array<any>>;
  feelingGroup: any;
  onEditOption: (opt: any) => void;
  onCreateOption: (groupId: string, parentId?: string) => void;
  onArchiveFeeling: (id: string) => void;
  onRestoreFeeling: (id: string) => void;
  isAdmin: boolean;
}

export function MoodFeelingCard({
  moodOptions,
  feelingOptions,
  feelingsByMood,
  feelingGroup,
  onEditOption,
  onCreateOption,
  onArchiveFeeling,
  onRestoreFeeling,
  isAdmin,
}: MoodFeelingCardProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Card data-testid="mood-feeling-section">
      <CardContent className="p-4 space-y-1">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <div className="flex items-center gap-3">
            <CollapsibleTrigger asChild>
              <button
                className="flex items-center gap-3 flex-1 min-w-0 text-left cursor-pointer hover:opacity-80 transition-opacity"
                data-testid="trigger-mood-feeling"
              >
                <Lock className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-foreground">
                      Mood & Feelings
                    </span>
                    <Badge
                      variant="outline"
                      className="text-[10px] no-default-hover-elevate no-default-active-elevate"
                    >
                      {moodOptions.length} moods
                    </Badge>
                    <Badge
                      variant="outline"
                      className="text-[10px] no-default-hover-elevate no-default-active-elevate"
                    >
                      {feelingOptions.length} feelings
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Each mood contains its associated feelings. Moods are
                    system-protected.
                  </p>
                </div>
                <ChevronDown
                  className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                />
              </button>
            </CollapsibleTrigger>
          </div>
          <CollapsibleContent>
            <div className="space-y-2 mt-3">
              {moodOptions.map((mood) => {
                const childFeelings = feelingsByMood.get(mood.id) || [];
                return (
                  <MoodFeelingCollapsible
                    key={mood.id}
                    mood={mood}
                    feelings={childFeelings}
                    onEditMood={onEditOption}
                    onEditFeeling={onEditOption}
                    onAddFeeling={() =>
                      feelingGroup && onCreateOption(feelingGroup.id, mood.id)
                    }
                    onArchiveFeeling={onArchiveFeeling}
                    onRestoreFeeling={onRestoreFeeling}
                    isAdmin={isAdmin}
                  />
                );
              })}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}
